import { formatCurrency } from '../../../utils/currency';
import GapMarker from './GapMarker';
import { FaCheck, FaMinus, FaMessage } from 'react-icons/fa6';
import { MdCheckCircle } from 'react-icons/md';

export interface QuoteColumnProps {
  providerName: string;
  quotedAtLabel: string;
  total: number;
  totalNote?: string;
  lineItems?: { label: string; sub?: string; amount?: number }[];
  included?: string[];
  excluded?: string[];
  gaps: string[];
  onAccept?: () => void;
  onAskQuestion?: () => void;
  className?: string;
  /** Add-on services subtotal (charged on top of line items). */
  addonSubtotal?: number;
  /** Add-on services detail lines (optional — shown as line entries). */
  additionalServices?: Array<{
    id: string;
    serviceItem: string;
    price: number;
  }>;
  /** Flat/percent adjustment applied to the subtotal. */
  adjustment?: {
    type: string;
    value: number;
    mode?: 'percent' | 'fixed';
    reason?: string;
  };
  /** Final quoted price the customer will pay. */
  quotedPrice?: number;
}

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const QuoteColumn = ({
  providerName,
  quotedAtLabel,
  total,
  totalNote,
  lineItems = [],
  included = [],
  excluded = [],
  gaps,
  onAccept,
  onAskQuestion,
  className = '',
  addonSubtotal = 0,
  additionalServices = [],
  adjustment,
}: QuoteColumnProps) => {
  // ── Totals math (mirrors LineItemsBreakdown) ──
  const subtotal = lineItems.reduce(
    (sum, li) => sum + (typeof li.amount === 'number' ? li.amount : 0),
    0
  );

  const isPercentAdjustment = adjustment?.mode === 'percent';
  const adjustmentAmount = adjustment
    ? isPercentAdjustment
      ? (subtotal * adjustment.value) / 100
      : adjustment.value
    : 0;

  const hasBreakdown =
    lineItems.length > 0 || addonSubtotal > 0 || Boolean(adjustment);

  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden ${className}`}
    >
      {/* Header Section */}
      <div className="p-4 bg-slate-50/80 border-b border-slate-100">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-8 h-8 rounded-full bg-[#163544] text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-2xs">
            {initials(providerName)}
          </span>
          <span className="text-sm font-bold text-slate-800 truncate">
            {providerName}
          </span>
        </div>

        <p className="text-2xl font-black text-slate-900 tabular-nums tracking-tight leading-tight break-words">
          {formatCurrency(total)}
        </p>

        {totalNote && (
          <p className="text-xs font-semibold text-slate-500 mt-1">
            {totalNote}
          </p>
        )}
        <p className="text-xs font-medium text-slate-400 mt-0.5">
          {quotedAtLabel}
        </p>
      </div>

      {/* Line Items + Price Breakdown Section */}
      {hasBreakdown && (
        <div className="p-4 border-b border-slate-100/80 space-y-2">
          <p className="text-[10.5px] font-bold tracking-wider uppercase text-slate-400 mb-2">
            Line Items
          </p>

          {/* Base line items */}
          {lineItems.length > 0 && (
            <div className="space-y-1.5">
              {lineItems.map((li, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-start gap-2.5 p-2 rounded-xl bg-slate-50/60 border border-slate-100/80 text-xs"
                >
                  <span className="text-slate-700 font-medium min-w-0 flex-1 truncate">
                    {li.label}
                    {li.sub && (
                      <span className="block text-[11px] text-slate-400 font-normal mt-0.5">
                        {li.sub}
                      </span>
                    )}
                  </span>
                  <span className="text-slate-900 font-semibold tabular-nums whitespace-nowrap">
                    {typeof li.amount === 'number'
                      ? formatCurrency(li.amount)
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Totals breakdown */}
          <div className="pt-2 space-y-1.5 border-t border-slate-100 mt-2">
            {/* Subtotal */}
            {lineItems.length > 0 && (
              <div className="flex justify-between text-[11px] text-slate-500">
                <span>Subtotal</span>
                <span className="font-medium text-slate-700 tabular-nums">
                  {formatCurrency(subtotal)}
                </span>
              </div>
            )}

            {/* Add-on services */}
            {addonSubtotal > 0 && (
              <>
                {additionalServices.length > 0 ? (
                  additionalServices.map((svc) => (
                    <div
                      key={svc.id}
                      className="flex justify-between text-[11px] text-slate-600"
                    >
                      <span className="min-w-0 truncate pr-2">
                        + {svc.serviceItem}
                      </span>
                      <span className="font-medium tabular-nums whitespace-nowrap">
                        {formatCurrency(svc.price)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between text-[11px] text-slate-600">
                    <span>Add-on Services</span>
                    <span className="font-medium tabular-nums">
                      {formatCurrency(addonSubtotal)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Adjustment (discount / surcharge) */}
            {adjustment && (
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-500 capitalize">
                  {adjustment.type}
                  {adjustment.reason && (
                    <span className="text-slate-400">
                      {' '}
                      ({adjustment.reason})
                    </span>
                  )}
                </span>
                <span
                  className={`font-medium tabular-nums ${
                    adjustment.type === 'discount'
                      ? 'text-emerald-600'
                      : 'text-slate-800'
                  }`}
                >
                  {adjustment.type === 'discount' ? '−' : '+'}
                  {formatCurrency(Math.abs(adjustmentAmount))}
                  {isPercentAdjustment && ` (${adjustment.value}%)`}
                </span>
              </div>
            )}

            {/* Quoted Price (final) */}
            <div className="flex justify-between items-center pt-2 mt-1 border-t border-slate-100">
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                Quoted Price
              </span>
              <span className="text-sm font-black text-primary tabular-nums">
                {formatCurrency(total)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Included Items Section */}
      <div className="p-4 border-b border-slate-100/80 space-y-2">
        <p className="text-[10.5px] font-bold tracking-wider uppercase text-slate-400 mb-2">
          Included
        </p>
        {included.length > 0 ? (
          <div className="space-y-1.5">
            {included.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-xs text-slate-700 font-medium"
              >
                <FaCheck className="text-emerald-600 shrink-0" size={12} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs italic text-slate-400">Not itemised</p>
        )}
      </div>

      {/* Excluded Items Section */}
      <div className="p-4 border-b border-slate-100/80 space-y-2">
        <p className="text-[10.5px] font-bold tracking-wider uppercase text-slate-400 mb-2">
          Excluded
        </p>
        {excluded.length > 0 ? (
          <div className="space-y-1.5">
            {excluded.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-xs text-slate-600"
              >
                <FaMinus className="text-slate-400 shrink-0" size={10} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-500 font-normal leading-relaxed">
            Nothing excluded — total covers job as described.
          </p>
        )}
      </div>

      {/* Gap Marker */}
      <div className="p-4 border-b border-slate-100/80 bg-slate-50/50">
        <GapMarker gaps={gaps} onAskAboutThis={onAskQuestion} />
      </div>

      {/* Footer Actions */}
      <div className="p-4 mt-auto space-y-2 bg-white">
        {onAccept && (
          <button
            type="button"
            onClick={onAccept}
            className="w-full h-10 rounded-xl bg-primary hover:bg-[#0081a0] text-white text-xs font-bold transition-all shadow-2xs hover:shadow-xs flex items-center justify-center gap-1.5 active:scale-[0.99]"
          >
            <MdCheckCircle size={15} />
            Accept {providerName}
          </button>
        )}
        {onAskQuestion && (
          <button
            type="button"
            onClick={onAskQuestion}
            className="w-full h-9 rounded-xl border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700 transition-all flex items-center justify-center gap-1.5"
          >
            <FaMessage className="text-slate-400" size={12} />
            Ask a question
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteColumn;
